import { h, mount, clear } from "@/lib/dom";
import { toast } from "@/components/ui";
import { topicsService, pbErrorMessage } from "@/services";
import { topicSources } from "@/services/source.service";
import { recordExercise } from "@/services/exercise-history.service";
import { markPracticed } from "@/services/streak.service";
import { addErrorCard } from "@/services/error-deck.service";
import { fetchExamSource } from "@/features/klausur/webSource";
import { buildPaper, type ExamPaper } from "@/features/klausur/paper";
import { gradeExam } from "@/features/klausur/correction";
import { aiTroubleCard } from "@/features/learn/aiHealth";
import { pfRoot, pfHead, pfLoading, pfBig, pfButton, pfEmpty, onEnter } from "@/features/pfad/shell";
import { renderMarkdown } from "@/markdown";
import { examResultKey } from "./ExamResultPage";
import { navigate, type RouteContext } from "@/app/router";
import type { Topic } from "@/types/models";

const KEY = "lernapp.klausursetup.v1";
const SIZES = [
  { tasks: 2, label: "Kurz", min: 45 },
  { tasks: 3, label: "Normal", min: 90 },
  { tasks: 4, label: "Lang", min: 135 },
];

export async function ExamPage(ctx: RouteContext): Promise<HTMLElement> {
  const topicId = ctx.params.id;
  const toLearn = () => navigate(`/topic/${topicId}/learn`);

  const head = pfHead(toLearn, { streak: false });
  const stage = h("div", { class: "pf-stage" });
  const foot = h("div", { class: "pf-foot" });
  const root = pfRoot(head.el, stage, foot);
  clear(foot);

  let topic: Topic;
  try {
    topic = await topicsService.get(topicId);
  } catch (err) {
    mount(stage, h("div", { class: "pf-body" }, pfEmpty("Thema nicht gefunden", pbErrorMessage(err), pfButton("Zurück", toLearn, "gray"))));
    return root;
  }

  let sizeIdx = Number(localStorage.getItem(KEY) ?? 1) || 1;
  let minutes = SIZES[sizeIdx]?.min ?? 90;

  setup();
  return root;

  function setup(): void {
    head.set(0, 0, 1);
    const minInput = h("input", { class: "pf-num", type: "number", min: 15, max: 300, step: 5, value: String(minutes) }) as HTMLInputElement;
    const btns = SIZES.map((s, i) =>
      h("button", {
        type: "button", class: i === sizeIdx ? "on" : "",
        onclick: () => {
          sizeIdx = i;
          minutes = s.min;
          minInput.value = String(s.min);
          btns.forEach((b, j) => b.classList.toggle("on", j === i));
        },
      }, h("span", null, s.label), h("small", null, `${s.tasks} Aufgaben · ${s.min} min`))
    );

    mount(stage, h("div", { class: "pf-body" },
      h("div", { class: "pf-setup" },
        h("h1", null, "Probeklausur"),
        h("p", { class: "lead" }, `${topic.title} · echtes Quellenmaterial aus dem Netz, Timer und Korrektur mit Randnotizen`),
        h("div", { class: "pf-field" }, h("label", null, "Umfang"), h("div", { class: "pf-seg tall" }, ...btns)),
        h("div", { class: "pf-field" }, h("label", null, "Bearbeitungszeit (Minuten)"), minInput))));

    const go = () => {
      minutes = Math.max(10, Math.min(Number(minInput.value) || 90, 300));
      localStorage.setItem(KEY, String(sizeIdx));
      void build();
    };
    onEnter(root, go);
    mount(foot, h("div", { class: "pf-foot-in" }, pfBig("Klausur erstellen", go)));
  }

  async function build(): Promise<void> {
    clear(foot);
    mount(stage, h("div", { class: "pf-body" }, pfLoading("Quellenmaterial wird gesucht", "Originaltext aus Wikisource/Wikipedia, dann die Aufgaben dazu.")));
    try {
      const src = await topicSources(topic);
      const web = await fetchExamSource(topic.title, src, topic.examType || undefined);
      const paper = await buildPaper(topic.title, web, src, topic.examType || undefined, SIZES[sizeIdx].tasks);
      if (!paper.tasks.length) throw new Error("Es ließen sich keine Aufgaben erstellen.");
      run(paper);
    } catch (err) {
      mount(stage, h("div", { class: "pf-body" }, h("div", { class: "pf-setup" }, aiTroubleCard(pbErrorMessage(err)))));
      mount(foot, h("div", { class: "pf-foot-in" },
        h("div", { class: "pf-actions" },
          pfButton("Zurück", setup, "ghost"),
          pfBig("Nochmal", () => void build(), "blue"))));
    }
  }

  function run(paper: ExamPaper): void {
    let left = minutes * 60;
    const timer = h("span", { class: "pf-timer" }, clock(left));
    clear(head.right);
    head.right.appendChild(timer);
    const tick = window.setInterval(() => {
      if (!document.body.contains(timer)) return window.clearInterval(tick);
      left = Math.max(0, left - 1);
      timer.textContent = clock(left);
      timer.classList.toggle("low", left <= 300);
      head.set(minutes * 60 - left, 0, minutes * 60);
      if (!left) window.clearInterval(tick);
    }, 1000);

    const sheet = h("textarea", { class: "pf-sheet", placeholder: "Deine Bearbeitung …" }) as HTMLTextAreaElement;
    const words = h("span", { class: "pf-count" }, "0 Wörter");
    sheet.addEventListener("input", () => {
      words.textContent = `${sheet.value.trim().split(/\s+/).filter(Boolean).length} Wörter`;
    });

    const points = paper.tasks.reduce((n, t) => n + t.points, 0);

    mount(stage, h("div", { class: "pf-body wide" },
      h("div", { class: "pf-exam" },
        h("div", { class: "pf-exam-tasks" },
          ...paper.tasks.map((t, i) =>
            h("div", { class: "pf-exam-card task" },
              h("div", { class: "num" }, `Aufgabe ${i + 1} · ${t.points} P.`),
              h("div", { class: "meta" }, `AFB ${"I".repeat(t.afb)} · ${t.operator}`),
              h("div", { class: "markdown-body", html: renderMarkdown(t.question) })))),
        h("div", { class: "pf-exam-sheet" },
          h("div", { class: "pf-exam-bar" },
            h("span", null, `${paper.tasks.length} Aufgaben · ${points} Punkte`),
            h("span", { class: "spacer" }), words),
          sheet),
        h("div", { class: "pf-exam-mat" },
          h("div", { class: "pf-exam-card" },
            h("h3", null, paper.sourceTitle),
            h("div", { class: "markdown-body", html: renderMarkdown(paper.source) }),
            h("p", { class: "pf-cite" }, paper.citation,
              paper.sourceUrl ? h("a", { href: paper.sourceUrl, target: "_blank", rel: "noreferrer" }, " ↗") : null))))));

    mount(foot, h("div", { class: "pf-foot-in" }, pfBig("Abgeben & korrigieren", submit)));

    async function submit(): Promise<void> {
      const answer = sheet.value.trim();
      if (!answer) return toast("Das Blatt ist leer.", "error");
      window.clearInterval(tick);
      clear(foot);
      mount(stage, h("div", { class: "pf-body" }, pfLoading("Deine Klausur wird korrigiert", "Randnotizen, Bewertungsmatrix und Note.")));
      try {
        const grade = await gradeExam(paper, answer);
        grade.topicId = topicId;
        grade.topicTitle = topic.title;
        sessionStorage.setItem(examResultKey(topicId), JSON.stringify(grade));
        recordExercise(topicId, "klausur", "Probeklausur", grade.percent, 100);
        for (const n of grade.notes ?? []) {
          if (n.mark !== "+" && n.mark !== "++") addErrorCard(topicId, `Klausur: „${n.quote}"`, `${n.note}${n.better ? ` – besser: ${n.better}` : ""}`);
        }
        for (const t of grade.improve ?? []) addErrorCard(topicId, `Klausur-Tipp: ${t}`, t);
        markPracticed();
        navigate(`/topic/${topicId}/exam-result`);
      } catch (err) {
        mount(stage, h("div", { class: "pf-body" }, h("div", { class: "pf-setup" }, aiTroubleCard(pbErrorMessage(err)))));
        mount(foot, h("div", { class: "pf-foot-in" }, pfBig("Nochmal abgeben", submit, "blue")));
      }
    }
  }
}

function clock(s: number): string {
  const hh = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return hh ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`;
}
